import { lazy, Suspense } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/hooks/useAuth";
import { Layout } from "@/components/layout/Layout";
import { LangLayout } from "@/i18n/LangLayout";
import Index from "./pages/Index.tsx";

// Lazy-load secondary pages to reduce initial JS bundle
const About = lazy(() => import("./pages/About.tsx"));
const Practice = lazy(() => import("./pages/Practice.tsx"));
const Contact = lazy(() => import("./pages/Contact.tsx"));
const Blog = lazy(() => import("./pages/Blog.tsx"));
const Article = lazy(() => import("./pages/Article.tsx"));
const Media = lazy(() => import("./pages/Media.tsx"));
const NotFound = lazy(() => import("./pages/NotFound.tsx"));
const Auth = lazy(() => import("./pages/Auth.tsx"));
const Accessibility = lazy(() => import("./pages/Accessibility.tsx"));
const Terms = lazy(() => import("./pages/Terms.tsx"));
const AdminLayout = lazy(() => import("@/components/admin/AdminLayout").then(m => ({ default: m.AdminLayout })));
const Dashboard = lazy(() => import("./pages/admin/Dashboard.tsx"));
const Leads = lazy(() => import("./pages/admin/Leads.tsx"));
const Articles = lazy(() => import("./pages/admin/Articles.tsx"));
const Testimonials = lazy(() => import("./pages/admin/Testimonials.tsx"));
const MediaAdmin = lazy(() => import("./pages/admin/Media.tsx"));
const UsersAdmin = lazy(() => import("./pages/admin/Users.tsx"));
const AuditLog = lazy(() => import("./pages/admin/AuditLog.tsx"));

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <HelmetProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <AuthProvider>
            <Suspense fallback={null}>
            <Routes>
              {/* Lang-prefixed public routes */}
              <Route path="/:lang" element={<LangLayout />}>
                <Route element={<Layout />}>
                  <Route index element={<Index />} />
                  <Route path="about" element={<About />} />
                  <Route path="practice/:slug" element={<Practice />} />
                  <Route path="contact" element={<Contact />} />
                  <Route path="blog" element={<Blog />} />
                  <Route path="blog/:slug" element={<Article />} />
                  <Route path="media" element={<Media />} />
                  <Route path="accessibility" element={<Accessibility />} />
                  <Route path="terms" element={<Terms />} />
                </Route>
              </Route>

              {/* Redirect bare paths to /he */}
              <Route path="/" element={<Navigate to="/he" replace />} />
              <Route path="/about" element={<Navigate to="/he/about" replace />} />
              <Route path="/practice/:slug" element={<RedirectWithSlug base="practice" />} />
              <Route path="/contact" element={<Navigate to="/he/contact" replace />} />
              <Route path="/blog" element={<Navigate to="/he/blog" replace />} />
              <Route path="/blog/:slug" element={<RedirectWithSlug base="blog" />} />
              <Route path="/media" element={<Navigate to="/he/media" replace />} />
              <Route path="/accessibility" element={<Navigate to="/he/accessibility" replace />} />
              <Route path="/terms" element={<Navigate to="/he/terms" replace />} />

              {/* Non-lang routes */}
              <Route path="/auth" element={<Auth />} />
              <Route path="/admin" element={<AdminLayout />}>
                <Route index element={<Dashboard />} />
                <Route path="leads" element={<Leads />} />
                <Route path="articles" element={<Articles />} />
                <Route path="testimonials" element={<Testimonials />} />
                <Route path="media" element={<MediaAdmin />} />
                <Route path="users" element={<UsersAdmin />} />
                <Route path="audit" element={<AuditLog />} />
              </Route>
              <Route path="*" element={<NotFound />} />
            </Routes>
            </Suspense>
          </AuthProvider>
        </BrowserRouter>
      </TooltipProvider>
    </HelmetProvider>
  </QueryClientProvider>
);

/** Helper for slug-based redirects */
import { useParams } from "react-router-dom";
function RedirectWithSlug({ base }: { base: string }) {
  const { slug } = useParams();
  return <Navigate to={`/he/${base}/${slug}`} replace />;
}

export default App;
